// The WORD-LINES painter — word↔word and word↔user connection lines
// (product-canon file 86 §3 "K-D3", via the electric signature file 94 §2 and
// the lights-not-cells direction file 83).
//
// WHAT CANON ASKS FOR. File 86 makes a key word a first-class object with
// connection points, and requires the rendering to SHOW those points and the
// lines between them:
//
//   - word ↔ word — "the same key word … appearing on two takes is a drawable
//     connection; shared language between takes is signal, shown as a line
//     between the words, not only as a hidden join." (file 86 §3)
//   - word ↔ user — "a key word connects to the person whose take carries it
//     (the take is threaded to its author's mark); so a word reaches a person
//     through its take." (file 86 §3)
//
// The render layer already emits every key word as a first-class object —
// `<li class="keyword word" data-object="keyword" data-word=… data-take=…
// data-hand=…>` in challenge.gohtml — and the template comment there says the
// words are emitted "so the field can LATER signal word-to-word, word-to-user".
// This module is that later: it draws the lines. It needs NO new render data —
// `data-word` is the shared-language key, `data-take` threads a word to its
// take, and the take's own author point (the lit `.point` inside `li.take`) is
// the person's mark a word reaches through. So a word→user line is a word node
// to its take's point, with no server change.
//
// HOW IT DRAWS — the electric signature, reused, not reinvented (file 94 §2 /
// §2a). A word connection is not a fence or a chip: it is the same TWO-SIDED
// EMISSION meeting-in-the-middle the ties already speak. So this painter builds
// an SVG <line> for each connection, flags it exactly as the render layer flags
// a tie (`data-tie`, `data-voices`, `data-register`, `data-hand[-b]`), and hands
// it to electric.ts's `driveField` — which lights the line, draws the voice
// cues, emits each side's words inward, and settles the lit MEETING MARK in the
// middle, honouring reduced-motion in motion.ts once for the whole island. The
// word connections therefore speak the field's ONE motion language and wear the
// ONE charged aesthetic; there is no second line vocabulary.
//
// THE TWO REGISTERS (file 94 §2a), chosen by WHO is speaking:
//   - word ↔ user is HUMAN: a person's word reaches that person — a voice. Warm,
//     the take's hand, a portrait emitter. The word is end `a` (the speaker),
//     the take's point is end `b` (where the word lands on its author's mark);
//     `data-voices="a"` so only the word side emits.
//   - word ↔ word is MECHANICAL: shared LANGUAGE between two takes is structural
//     signal, not one person speaking — cool crisp lights and a quiet pulse, no
//     portrait, no mouth (file 94 §2a). Both sides are words; `data-voices="both"`.
//
// LIGHTS, NOT CELLS (file 83 §1). Lines are straight geometry between lit points;
// the glow and the meeting mark are the whole affordance; nothing is boxed,
// fenced or chipped, and NO cursor/hand/finger is ever drawn — the emission and
// the mark demonstrate. A word that shares no language and whose take point is
// absent gets no line: the quiet field stays quiet.
//
// LIVENESS (file 91 §2a). The lines are geometry over the live DOM, so they are
// recomputed whenever the field changes: an HTMX fragment swap (new words/takes
// appear) and a viewport resize both re-measure and re-paint, so a word that
// just arrived joins the shared-word lines live. The island entry wires the swap
// and the SSE ping to `repaint()`.
//
// SSR / test safe: no document access at import time; every DOM read is guarded.
// Scope discipline (sibling theme lane owns the CSS): this module writes NO CSS
// and touches no stylesheet — the overlay is positioned inline and all colour
// comes from the token `var(--…)` references electric.ts already consumes.

import { driveField, type LiveHandle } from "./electric.js";

const SVG_NS = "http://www.w3.org/2000/svg";

/** A measured word node: its connection key, its take, its hand, and the screen
 *  centre the line attaches to (in the overlay's own coordinate space). */
interface WordNode {
  /** The shared-language key: the normalised `data-word`. Two takes carrying the
   *  same key is the word↔word signal (file 86 §3). */
  key: string;
  /** The owning take id (`data-take`) — the thread to the word's author. */
  takeId: string | null;
  /** The author's hand (`data-hand`), for the human register's warmth. */
  hand: string | null;
  /** The word's connection point, in overlay coordinates. */
  at: Pt;
  /** The DOM node, so a word↔user line can find its take's author point. */
  el: HTMLElement;
}

interface Pt {
  x: number;
  y: number;
}

/** A handle to the painted word-lines, so a repaint can clear the previous set
 *  (electric liveness + the overlay) before drawing the new one. */
export interface WordLinesHandle {
  /** Re-measure the live DOM and re-draw every line. Call after a swap/resize. */
  repaint(): void;
  /** Remove the overlay and clear all liveness. */
  clear(): void;
}

/** Normalise a word to its shared-language key. File 77's keyword identity is the
 *  authority for synonyms/equivalents; absent that projection on the client, the
 *  honest client-side key is the trimmed, case-folded surface form, so "Market"
 *  on one take and "market" on another are the same shared word. We deliberately
 *  do NOT invent stemming/synonymy here — that is the server's settled identity
 *  (S-70), and guessing it on the client would draw a line canon did not. */
function wordKey(raw: string | null): string {
  return (raw ?? "").trim().toLowerCase();
}

/** The centre of an element's box, in the overlay's coordinate space. The
 *  overlay is absolutely positioned at the host's top-left, so a client rect is
 *  translated by the host's own client rect. */
function centreIn(el: Element, hostRect: DOMRect): Pt {
  const r = el.getBoundingClientRect();
  return {
    x: r.left + r.width / 2 - hostRect.left,
    y: r.top + r.height / 2 - hostRect.top,
  };
}

function round2(f: number): number {
  return Math.round(f * 100) / 100;
}

/** Find a take's author point — the lit `.point` inside `li.take[data-take=…]`
 *  — which is the person's mark a word reaches through (file 86 §3). Falls back
 *  to the take `<li>` centre if the point svg is absent (never null when the
 *  take exists), and returns null when the take itself is not in this field. */
function takePoint(host: HTMLElement, takeId: string | null, hostRect: DOMRect): Pt | null {
  if (!takeId) return null;
  // Attribute selectors need the value escaped (a take id is an int today, but
  // do not assume it). CSS.escape is ubiquitous; guard for headless anyway.
  const sel =
    typeof CSS !== "undefined" && typeof CSS.escape === "function"
      ? `li.take[data-take="${CSS.escape(takeId)}"]`
      : `li.take[data-take="${takeId}"]`;
  const take = host.querySelector<HTMLElement>(sel);
  if (!take) return null;
  const point = take.querySelector<SVGCircleElement>("svg .point") ?? take.querySelector<SVGCircleElement>("svg .point-body");
  return centreIn(point ?? take, hostRect);
}

/** Build an <line> flagged for electric.ts exactly as the render layer flags a
 *  tie, so `driveField` paints the two-sided emission over it. Endpoints are
 *  given both as x1/y1/x2/y2 AND as data-a/data-b "x,y" (electric reads either),
 *  so the line is self-describing. The stroke is the quiet ink at rest; electric
 *  lifts it to the charge colour while live. */
function connectionLine(
  a: Pt,
  b: Pt,
  opts: {
    register: "human" | "mechanical";
    voices: "both" | "a" | "b";
    handA?: string | null;
    handB?: string | null;
  },
): SVGLineElement {
  const line = document.createElementNS(SVG_NS, "line");
  line.setAttribute("class", "word-line");
  line.setAttribute("x1", String(round2(a.x)));
  line.setAttribute("y1", String(round2(a.y)));
  line.setAttribute("x2", String(round2(b.x)));
  line.setAttribute("y2", String(round2(b.y)));
  line.setAttribute("data-a", `${round2(a.x)},${round2(a.y)}`);
  line.setAttribute("data-b", `${round2(b.x)},${round2(b.y)}`);
  line.setAttribute("data-tie", "");
  line.setAttribute("data-voices", opts.voices);
  line.setAttribute("data-register", opts.register);
  if (opts.handA) line.setAttribute("data-hand", opts.handA);
  if (opts.handB) line.setAttribute("data-hand-b", opts.handB);
  // The resting line is a faint hairline of attachment (file 83: a line, not a
  // box). Colour comes from the token layer; a literal fallback keeps it visible
  // headless / before CSS loads. Weight is thin — a word connection is lighter
  // than a tie between whole takes.
  line.setAttribute("stroke", "var(--hair, var(--ink-quiet, #9aa89e))");
  line.setAttribute("stroke-width", "1");
  line.setAttribute("opacity", "0.5");
  return line;
}

/**
 * Paint the word lines for a field root. Builds (once) an absolutely-positioned
 * SVG overlay inside the host — behind the words so the lit words sit over their
 * connections — and returns a handle that re-measures and re-draws on demand.
 *
 * The host is the field root (the element the island mounts on, `#field`). The
 * overlay is sized to the host and recomputed on every `repaint()`. Because the
 * lines are measured from the live DOM, they follow the words wherever the CSS
 * flow lays them out, in any viewport or orientation.
 */
export function paintWordLines(host: HTMLElement): WordLinesHandle {
  // Guard: nothing to do without a DOM (SSR / import-time).
  if (typeof document === "undefined") {
    return { repaint() {}, clear() {} };
  }

  // The host must be a positioning context so the absolute overlay pins to its
  // top-left. If the author left it static, make it relative — this is an inline
  // style on the ISLAND's mount root, not a stylesheet edit (scope: no CSS).
  const computed = typeof getComputedStyle === "function" ? getComputedStyle(host).position : "";
  if (computed === "static" || computed === "") {
    host.style.position = "relative";
  }

  // One overlay, reused across repaints. It is aria-hidden (the lines are a
  // visual echo of the real word objects, which carry the semantics) and
  // pointer-events:none so it never intercepts a gesture meant for a word.
  let svg: SVGSVGElement | null = null;
  let live: LiveHandle | null = null;

  function ensureOverlay(): SVGSVGElement {
    if (svg && svg.isConnected) return svg;
    svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("class", "word-lines-overlay");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
    svg.style.position = "absolute";
    svg.style.left = "0";
    svg.style.top = "0";
    svg.style.width = "100%";
    svg.style.height = "100%";
    svg.style.pointerEvents = "none";
    svg.style.overflow = "visible";
    // Behind the words (which are flow content with their own stacking), so the
    // lit words read as ON their connections, not under them (lights, not cells).
    svg.style.zIndex = "0";
    // Insert as the host's first child so it paints first (behind the words).
    host.insertBefore(svg, host.firstChild);
    return svg;
  }

  function repaint(): void {
    // Clear prior liveness + geometry first (no leaked animations across swaps).
    live?.clear();
    live = null;

    const o = ensureOverlay();
    while (o.firstChild) o.removeChild(o.firstChild);

    const hostRect = host.getBoundingClientRect();
    // Size the viewBox to the host so coordinates are 1:1 CSS pixels.
    o.setAttribute("viewBox", `0 0 ${round2(hostRect.width)} ${round2(hostRect.height)}`);
    o.setAttribute("width", String(round2(hostRect.width)));
    o.setAttribute("height", String(round2(hostRect.height)));

    // Collect every key-word node present. Only `.keyword` objects with a
    // `data-word` are words (file 86); the firsts-demo / feed carry none here.
    const nodes: WordNode[] = [];
    host.querySelectorAll<HTMLElement>(".keyword[data-word]").forEach((el) => {
      const key = wordKey(el.getAttribute("data-word"));
      if (!key) return;
      // An invisible word (a collapsed <details> that is not open) has a zero
      // box; skip it so we do not draw a line to a point that is not shown. The
      // key-word SUMMARY is always visible, so shared-word lines between summary
      // words still draw; only the hidden full-body duplicates are skipped.
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return;
      nodes.push({
        key,
        takeId: el.getAttribute("data-take"),
        hand: el.getAttribute("data-hand"),
        at: centreIn(el, hostRect),
        el,
      });
    });

    // --- word ↔ user (human) ------------------------------------------------
    // Each visible word reaches the person who said it, through its take's
    // author point (file 86 §3). One line per word, word (end a, the speaker)
    // to its take's point (end b). The word emits; the point is where it lands
    // on its author's mark. Skip when the take point is not in this field (e.g.
    // a word with no owning take rendered here).
    const pointCache = new Map<string, Pt | null>();
    for (const n of nodes) {
      if (!n.takeId) continue;
      let p = pointCache.get(n.takeId);
      if (p === undefined) {
        p = takePoint(host, n.takeId, hostRect);
        pointCache.set(n.takeId, p);
      }
      if (!p) continue;
      // A word sitting almost on its own point (a tiny take) would draw a stub;
      // skip a degenerate near-zero-length line (nothing to show, no meeting).
      if (Math.hypot(p.x - n.at.x, p.y - n.at.y) < 6) continue;
      o.appendChild(
        connectionLine(n.at, p, {
          register: "human",
          voices: "a", // only the word (the person) speaks; the point is where it lands
          handA: n.hand,
          handB: n.hand,
        }),
      );
    }

    // --- word ↔ word (mechanical) ------------------------------------------
    // Shared language between takes is structural signal (file 86 §3): when the
    // same key word appears on two or more DIFFERENT takes, draw the line. We
    // chain the occurrences (each to the next) rather than a full clique, so
    // five takes sharing a word read as a legible thread of lights, not a dense
    // fence of O(n²) lines (file 83: lights, not cells — a fence is exactly the
    // failure). A word repeated within ONE take is not a cross-take signal, so
    // we group by key and connect across distinct takes only.
    const byKey = new Map<string, WordNode[]>();
    for (const n of nodes) {
      const list = byKey.get(n.key);
      if (list) list.push(n);
      else byKey.set(n.key, [n]);
    }
    byKey.forEach((group) => {
      // One representative node per distinct take for this word — the first
      // visible occurrence — so a word repeated in a take's summary AND its full
      // body does not self-connect. Order by vertical position so the chain
      // reads top-to-bottom down the field.
      const perTake = new Map<string, WordNode>();
      for (const n of group) {
        const t = n.takeId ?? `anon:${round2(n.at.x)},${round2(n.at.y)}`;
        if (!perTake.has(t)) perTake.set(t, n);
      }
      const reps = [...perTake.values()].sort((p, q) => p.at.y - q.at.y || p.at.x - q.at.x);
      if (reps.length < 2) return; // not shared across takes — no line
      for (let i = 0; i + 1 < reps.length; i++) {
        const a = reps[i];
        const b = reps[i + 1];
        o.appendChild(
          connectionLine(a.at, b.at, {
            register: "mechanical", // shared language is structure, not one voice
            voices: "both",
            handA: a.hand,
            handB: b.hand,
          }),
        );
      }
    });

    // Hand the whole overlay to the electric register, which paints the two-
    // sided emission + meeting mark over every flagged line and lights the field
    // with the one charged aesthetic. Reduced motion is honoured in motion.ts.
    live = driveField(o);
  }

  // First paint.
  repaint();

  // Re-measure on resize/orientation change so the lines follow the words the
  // CSS reflow moved. Coalesced to an animation frame so a resize drag does not
  // thrash. Guarded for headless (no window).
  let raf = 0;
  const onResize = () => {
    if (typeof requestAnimationFrame !== "function") {
      repaint();
      return;
    }
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      repaint();
    });
  };
  if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("resize", onResize);
  }

  return {
    repaint,
    clear() {
      if (raf && typeof cancelAnimationFrame === "function") cancelAnimationFrame(raf);
      if (typeof window !== "undefined" && typeof window.removeEventListener === "function") {
        window.removeEventListener("resize", onResize);
      }
      live?.clear();
      live = null;
      if (svg && svg.parentNode) svg.parentNode.removeChild(svg);
      svg = null;
    },
  };
}
